import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { isCapacitorNative } from '../platform/capacitor-platform';
import { LoggerService } from '../utils/logger.service';

/**
 * Observes online/offline state. On Capacitor uses @capacitor/network; on web uses navigator.onLine.
 * Used later by ProgressSyncService (PR4) to flush the outbox when connectivity returns.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  private readonly onlineSubject = new BehaviorSubject<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  private started = false;

  readonly connected$: Observable<boolean> = this.onlineSubject.asObservable();

  get isOnline(): boolean {
    return this.onlineSubject.value;
  }

  constructor(private zone: NgZone) {}

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    if (isCapacitorNative()) {
      try {
        const { Network } = await import('@capacitor/network');
        const status = await Network.getStatus();
        this.onlineSubject.next(status.connected);
        await Network.addListener('networkStatusChange', (s) => {
          this.zone.run(() => this.onlineSubject.next(s.connected));
        });
        LoggerService.log('[ConnectivityService] Capacitor network listener active');
        return;
      } catch (e) {
        LoggerService.error('[ConnectivityService] Capacitor network unavailable, using web fallback', e);
      }
    }

    const update = () => this.zone.run(() => this.onlineSubject.next(navigator.onLine));
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    update();
    LoggerService.log('[ConnectivityService] Web online/offline listeners active');
  }
}
